import { type Observable, ReplaySubject } from 'rxjs'
import { h, render } from 'vue'
import { ElDialog, ElButton } from 'element-plus'

/**
 * Dynamically generates an Element Plus confirm dialog and returns an Observable<boolean>
 * that emits true if confirmed, false if cancelled.
 *
 * @param message The message to display in the dialog
 * @param title Optional title for the dialog
 */
export function confirm({
  message = '',
  title = 'Confirm',
  cancelText = '取消',
  confirmText = '確定',
  showClose = false,
  showCancel = true
}): Observable<boolean | 'confirm' | 'cancel'> {
  const result = new ReplaySubject<boolean | 'confirm' | 'cancel'>()

  // Create a container for the dialog
  const container = document.createElement('div')
  document.body.appendChild(container)

  // Dialog state
  let visible = true

  // Close and cleanup
  const cleanup = () => {
    render(null, container)
    document.body.removeChild(container)
  }

  const btns = showCancel ? [
    h(
      ElButton,
      {
        onClick: () => {
          visible = false
          result.next('cancel')
          cleanup()
        },
        class: 'gray theme-1',
        plain: true
      },
      { default: () => cancelText }
    )
  ] : []

  btns.push(
    h(
      ElButton,
      {
        type: 'primary',
        onClick: () => {
          visible = false
          result.next('confirm')
          cleanup()
        },
        class: 'main theme-1'
      },
      { default: () => confirmText }
    )
  )

  // Dialog vNode
  const vNode = h(
    ElDialog,
    {
      modelValue: visible,
      'onUpdate:modelValue': (val: boolean) => {
        visible = val
        if (!val) {
          result.next(false)
          cleanup()
        }
      },
      title,
      closeOnClickModal: false,
      closeOnPressEscape: false,
      appendToBody: false,
      showClose,
      class: 'confirm-dialog',
      beforeClose: () => {
        result.next(false)
        cleanup()
      }
    },
    {
      default: () => h('div', message),
      footer: () => btns
    }
  )

  render(vNode, container)

  return result
}